import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { Card, Btn, Pill, Modal, Empty, Notice, inputCls } from "@/dashboard/ui";
import { RecipientPicker } from "@/dashboard/Marketing";
import { MessageSquare, Plus, Pencil, Trash2, Clock, XCircle, Eye } from "lucide-react";

const WA_CATEGORIES = [
  ["marketing", "Marketing"],
  ["appointment_reminder", "Appointment reminder"],
  ["appointment_confirmation", "Appointment confirmation"],
  ["follow_up", "Follow-up"],
];
const STATUS_TONE = { Draft: "grey", Scheduled: "black", Sending: "amber", Sent: "green", Failed: "red", Cancelled: "outline" };

export default function WhatsAppPanel() {
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [tplEditing, setTplEditing] = useState(null);
  const [campEditing, setCampEditing] = useState(null);
  const [scheduling, setScheduling] = useState(null);
  const [schedAt, setSchedAt] = useState("");
  const [audience, setAudience] = useState(null);
  const [tab, setTab] = useState("campaigns");

  const load = () => {
    api.get("/whatsapp/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/whatsapp/templates").then((r) => setTemplates(r.data.templates)).catch(() => setTemplates([]));
    api.get("/whatsapp/campaigns").then((r) => setCampaigns(r.data.campaigns)).catch(() => setCampaigns([]));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveTemplate = async () => {
    try {
      if (tplEditing.id) await api.put(`/whatsapp/templates/${tplEditing.id}`, tplEditing);
      else await api.post("/whatsapp/templates", tplEditing);
      toast.success("Template saved — it still needs Meta approval before it can be sent");
      setTplEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveCampaign = async () => {
    try {
      await api.post("/whatsapp/campaigns", campEditing);
      toast.success("Campaign draft saved");
      setCampEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const schedule = async () => {
    try {
      await api.post(`/whatsapp/campaigns/${scheduling.id}/schedule`, { scheduled_at: schedAt, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      toast.success("Scheduled — sends only after WhatsApp is connected and sending is approved");
      setScheduling(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="whatsapp-panel">
      {status && (
        <Notice testid="wa-status-note"
          title={status.state === "needs_setup" ? "WhatsApp Business needs setup" : "WhatsApp is configured, sending is off"}
          body={status.state === "needs_setup"
            ? "No WhatsApp Business credentials are configured. You can prepare templates and campaign drafts now — sending activates only after Meta credentials exist and the owner approves sending."
            : "Credentials present. Messages send only when the platform owner enables WhatsApp sending. Templates also need Meta approval."} />
      )}
      {status?.state === "needs_setup" && (
        <Card className="p-5" data-testid="wa-setup">
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Setup required (official WhatsApp Business Platform)</p>
          <ol className="mt-2 list-inside list-decimal space-y-1 text-xs text-neutral-600">
            {status.instructions.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <div className="flex rounded-full border border-neutral-300 p-0.5" data-testid="wa-subtabs">
          {[["campaigns", "Campaigns"], ["templates", "Templates"]].map(([id, l]) => (
            <button key={id} data-testid={`wa-subtab-${id}`} onClick={() => setTab(id)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${tab === id ? "bg-[#111111] text-white" : "text-neutral-500"}`}>{l}</button>
          ))}
        </div>
        <Btn data-testid="wa-new-button" onClick={() => tab === "templates" ? setTplEditing({ name: "", body: "", category: "marketing" }) : setCampEditing({ name: "", template_id: "", lead_ids: [], lead_statuses: [] })}>
          <Plus size={15} /> {tab === "templates" ? "New template" : "New campaign"}
        </Btn>
      </div>

      {tab === "templates" && (
        templates === null ? null : templates.length === 0 ? (
          <Empty testid="wa-templates-empty" icon={MessageSquare} title="No templates yet" body="Draft message templates here. Meta must approve a template before it can be sent to customers." />
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {templates.map((t) => (
              <Card key={t.id} className="p-5" data-testid={`wa-template-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-bold text-[#111111]">{t.name}</p>
                    <Pill tone="amber" data-testid={`wa-template-status-${t.id}`}>Pending Meta approval</Pill>
                  </div>
                  <div className="flex gap-1">
                    <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100" data-testid={`wa-template-edit-${t.id}`} onClick={() => setTplEditing({ ...t })}><Pencil size={14} /></button>
                    <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-600" data-testid={`wa-template-delete-${t.id}`}
                      onClick={async () => { if (window.confirm(`Delete template "${t.name}"?`)) { await api.delete(`/whatsapp/templates/${t.id}`); load(); } }}><Trash2 size={14} /></button>
                  </div>
                </div>
                <Pill tone="outline">{WA_CATEGORIES.find(([id]) => id === t.category)?.[1] || t.category}</Pill>
                <p className="mt-2 whitespace-pre-wrap text-xs text-neutral-600">{t.body}</p>
              </Card>
            ))}
          </div>
        )
      )}

      {tab === "campaigns" && (
        campaigns === null ? null : campaigns.length === 0 ? (
          <Empty testid="wa-campaigns-empty" icon={MessageSquare} title="No WhatsApp campaigns yet" body="Create a draft from a template and choose opted-in contacts. Nothing is sent until WhatsApp is connected and approved." />
        ) : (
          <div className="space-y-3">
            {campaigns.map((c) => (
              <Card key={c.id} className="p-5" data-testid={`wa-campaign-${c.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-bold text-[#111111]">{c.name}</p>
                    <Pill tone={STATUS_TONE[c.status]} data-testid={`wa-status-${c.id}`}>{c.status}</Pill>
                    {c.status === "Scheduled" && <Pill tone="outline">{c.scheduled_at?.replace("T", " ").slice(0, 16)} {c.timezone}</Pill>}
                  </div>
                  <div className="flex gap-1">
                    <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100" title="Audience" data-testid={`wa-audience-${c.id}`}
                      onClick={async () => setAudience({ c, ...(await api.get(`/whatsapp/campaigns/${c.id}/audience`)).data })}><Eye size={14} /></button>
                    <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-600" data-testid={`wa-delete-${c.id}`}
                      onClick={async () => { if (window.confirm(`Delete campaign "${c.name}"?`)) { await api.delete(`/whatsapp/campaigns/${c.id}`); load(); } }}><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["Draft", "Failed", "Cancelled"].includes(c.status) && (
                    <Btn variant="ghost" data-testid={`wa-schedule-${c.id}`} onClick={() => { setScheduling(c); setSchedAt(""); }}>
                      <Clock size={13} className="mr-1 inline" /> Schedule
                    </Btn>
                  )}
                  {c.status === "Scheduled" && (
                    <Btn variant="ghost" data-testid={`wa-cancel-${c.id}`} onClick={async () => { await api.post(`/whatsapp/campaigns/${c.id}/cancel`); toast.success("Schedule cancelled"); load(); }}>
                      <XCircle size={13} className="mr-1 inline" /> Cancel schedule
                    </Btn>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      {tplEditing && (
        <Modal title={tplEditing.id ? "Edit template" : "New message template"} onClose={() => setTplEditing(null)} testid="wa-template-modal">
          <div className="space-y-3">
            <input className={inputCls} placeholder="Template name *" value={tplEditing.name} data-testid="wa-tpl-name" onChange={(e) => setTplEditing((t) => ({ ...t, name: e.target.value }))} />
            <select className={inputCls} value={tplEditing.category} data-testid="wa-tpl-category" onChange={(e) => setTplEditing((t) => ({ ...t, category: e.target.value }))}>
              {WA_CATEGORIES.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
            </select>
            <textarea rows={5} className={inputCls} placeholder="Message body * — e.g. Hi {{1}}, your appointment is confirmed for {{2}}." value={tplEditing.body} data-testid="wa-tpl-body" onChange={(e) => setTplEditing((t) => ({ ...t, body: e.target.value }))} />
            <p className="text-[11px] text-neutral-400">Templates stay as drafts here until they are submitted to and approved by Meta.</p>
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setTplEditing(null)}>Cancel</Btn>
              <Btn data-testid="wa-tpl-save" disabled={!tplEditing.name.trim() || !tplEditing.body.trim()} onClick={saveTemplate}>Save template</Btn>
            </div>
          </div>
        </Modal>
      )}

      {campEditing && (
        <Modal title="New WhatsApp campaign" onClose={() => setCampEditing(null)} testid="wa-campaign-modal">
          <div className="space-y-3">
            <input className={inputCls} placeholder="Campaign name *" value={campEditing.name} data-testid="wa-c-name" onChange={(e) => setCampEditing((c) => ({ ...c, name: e.target.value }))} />
            <div>
              <span className="mb-1 block text-xs font-semibold text-neutral-600">Template *</span>
              <select className={inputCls} value={campEditing.template_id} data-testid="wa-c-template" onChange={(e) => setCampEditing((c) => ({ ...c, template_id: e.target.value }))}>
                <option value="">Choose a template…</option>
                {(templates || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <span className="mb-1 block text-xs font-semibold text-neutral-600">Recipients — only contacts who opted in to messaging are included</span>
              <RecipientPicker selected={campEditing.lead_ids} onChange={(ids) => setCampEditing((c) => ({ ...c, lead_ids: ids }))} />
            </div>
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setCampEditing(null)}>Cancel</Btn>
              <Btn data-testid="wa-c-save" disabled={!campEditing.name.trim() || !campEditing.template_id} onClick={saveCampaign}>Save draft</Btn>
            </div>
          </div>
        </Modal>
      )}

      {scheduling && (
        <Modal title={`Schedule — ${scheduling.name}`} onClose={() => setScheduling(null)} testid="wa-schedule-modal">
          <div className="space-y-3">
            <input type="datetime-local" className={inputCls} value={schedAt} data-testid="wa-schedule-at" onChange={(e) => setSchedAt(e.target.value)} />
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setScheduling(null)}>Cancel</Btn>
              <Btn data-testid="wa-schedule-confirm" disabled={!schedAt} onClick={schedule}>Schedule</Btn>
            </div>
          </div>
        </Modal>
      )}

      {audience && (
        <Modal title={`Audience — ${audience.c.name}`} onClose={() => setAudience(null)} testid="wa-audience-modal">
          <p className="text-sm font-bold text-[#111111]" data-testid="wa-audience-count">{audience.count} opted-in contact{audience.count === 1 ? "" : "s"}</p>
          <div className="mt-2 space-y-1">
            {audience.sample.map((l) => <p key={l.id} className="text-xs text-neutral-600">{l.full_name} · {l.phone}</p>)}
          </div>
          <p className="mt-3 text-[11px] text-neutral-400">{audience.note}</p>
        </Modal>
      )}
    </div>
  );
}
